import type { GuidedEditUpdateBudget } from "@common/interfaces/adsManagerGuidedEdit";
import type { MarketingCampaignBudgetSegment } from "@common/interfaces/KYDMarketing";
import { scheduleInputToUtcIso } from "@common/utils/adsetSchedule";
import { minorAmountToPrice } from "@common/utils/minorAmountToPrice";
import type { BudgetData } from "../components/GuidedBudgetEditor";

function dollarsToMinorAmount(
  major: number | null | undefined
): number | null {
  if (major == null || Number.isNaN(Number(major))) return null;
  return Math.round(Number(major) * 100);
}

/** Form dollars → guided-edit wire for `daily` / `lifetime` (minor units on `amount`). */
export function wireDailyLifetimeBudgetFromFormDollars(
  budget: BudgetData
): GuidedEditUpdateBudget {
  const budgetType: "daily" | "lifetime" =
    budget.type === "daily" ? "daily" : "lifetime";
  const major = budget.amount?.amount;
  const minor = dollarsToMinorAmount(major);
  return {
    type: budgetType,
    amount: {
      amount: minor,
      currency: budget.amount?.currency ?? "USD",
    },
  };
}

/** Form dollars + event-wall phase times → guided-edit wire for `scheduled` (ISO segments, minor amounts). */
export function wireScheduledBudgetFromFormDollars(
  budget: BudgetData,
  eventTimezone: string
): GuidedEditUpdateBudget {
  const currency = budget.amount?.currency ?? "USD";
  const tz = (eventTimezone ?? "").trim() || "UTC";
  const segments: MarketingCampaignBudgetSegment[] = (
    budget.segments ?? []
  ).map((s) => {
    const minor = dollarsToMinorAmount(s.amount?.amount);
    const minorSafe = minor ?? 0;
    const cur = s.amount?.currency ?? currency;
    return {
      time_start: scheduleInputToUtcIso(s.time_start, tz) ?? s.time_start,
      time_end: scheduleInputToUtcIso(s.time_end, tz) ?? s.time_end,
      amount: minorAmountToPrice(minorSafe, cur),
    };
  });
  return {
    type: "scheduled",
    segments,
  };
}

/**
 * Convert form {@link BudgetData} (major units, e.g. USD dollars) to API minor units (cents for USD).
 * Dispatches to {@link wireDailyLifetimeBudgetFromFormDollars} or {@link wireScheduledBudgetFromFormDollars}.
 */
export function wireGuidedEditBudgetFromFormDollars(
  budget: BudgetData,
  eventTimezone?: string
): GuidedEditUpdateBudget {
  if (budget.type === "scheduled") {
    return wireScheduledBudgetFromFormDollars(
      budget,
      eventTimezone ?? "UTC"
    );
  }
  return wireDailyLifetimeBudgetFromFormDollars(budget);
}
